export const parseMeta = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

/** API MySQL trả UTC không có Z — parse đúng rồi hiển thị giờ Việt Nam. */
export const parseServerDateTime = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (/[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
        const date = new Date(raw);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    const iso = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const date = new Date(`${iso}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatInVietnam = (date) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Ho_Chi_Minh',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
};

export const formatAlertDateTime = (value) => {
    const date = parseServerDateTime(value);
    if (!date) return '';
    return formatInVietnam(date);
};

export const formatMedicalDateTime = (value) => {
    if (!value) return '—';
    const date = parseServerDateTime(value);
    if (!date) return String(value);
    return formatInVietnam(date);
};

export const formatMedicalDate = (value) => {
    if (!value) return '—';
    const date = parseServerDateTime(value);
    if (date) {
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Asia/Ho_Chi_Minh',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).formatToParts(date);
        const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
        return `${get('day')}/${get('month')}/${get('year')}`;
    }
    const raw = String(value).split('T')[0];
    const parts = raw.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return raw || '—';
};

export const formatRemindBefore = (raw) => {
    const minutes = typeof raw === 'number' ? raw : parseInt(String(raw || ''), 10);
    if (!Number.isFinite(minutes) || minutes <= 0) return '—';
    if (minutes < 60) return `${minutes} phút`;
    if (minutes % 60 === 0) return `${minutes / 60} giờ`;
    return `${Math.floor(minutes / 60)} giờ ${minutes % 60} phút`;
};

export const resolveAlertAction = (alert, meta, ctx = {}) => {
    const kind = (meta?.kind || '').toString();
    if (kind === 'med_followup' && meta?.logId) {
        return { type: 'followup' };
    }
    if (kind === 'med_taken' || kind === 'med_followup_result') {
        const elderlyId = alert?.elderlyId ?? meta?.elderlyId ?? meta?.elderly_id ?? ctx.elderlyId;
        if (elderlyId) return { type: 'medication', elderlyId: String(elderlyId) };
    }
    if (kind === 'missed_checkin_3d' || kind === 'daily_checkin_done') {
        const elderlyId = alert?.elderlyId ?? meta?.elderlyId ?? meta?.elderly_id ?? ctx.elderlyId;
        if (elderlyId) return { type: 'relatives', elderlyId: String(elderlyId) };
    }
    if (kind === 'revisit' || alert?.type === 'appointment') {
        const recordId = meta?.recordId ?? meta?.appointmentId ?? meta?.id;
        if (recordId) return { type: 'revisit', recordId: String(recordId), meta };
    }
    if (kind === 'med_reminder' || alert?.type === 'medication') {
        const elderlyId = alert?.elderlyId ?? meta?.elderlyId ?? meta?.elderly_id ?? ctx.elderlyId;
        if (elderlyId) return { type: 'medication', elderlyId: String(elderlyId) };
    }
    return { type: 'none' };
};

export const buildRevisitPath = (recordId, meta = {}) => {
    const qp = new URLSearchParams();
    if (meta.revisitAt) qp.set('time', meta.revisitAt);
    if (meta.hospitalName) qp.set('place', meta.hospitalName);
    if (meta.diagnosis) qp.set('note', meta.diagnosis);
    if (meta.doctorNotes) qp.set('doctorNotes', meta.doctorNotes);
    if (meta.visitDate) qp.set('visitDate', meta.visitDate);
    if (meta.remindBefore != null) qp.set('remindBefore', String(meta.remindBefore));
    if (meta.elderlyName) qp.set('elderlyName', meta.elderlyName);
    const qs = qp.toString();
    return `/revisit-appointment/${recordId}${qs ? `?${qs}` : ''}`;
};

export const revisitSummaryFromMeta = (meta = {}) => {
    const parts = [];
    if (meta.revisitAt) parts.push(formatMedicalDateTime(meta.revisitAt));
    if (meta.hospitalName) parts.push(meta.hospitalName);
    if (meta.diagnosis) parts.push(meta.diagnosis);
    return parts.filter(Boolean).join(' · ');
};
